import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiCalendar, FiBookOpen, FiUsers, FiBriefcase, FiAward, FiStar } from 'react-icons/fi';
import './Timeline.css'; // Import the CSS file

const Timeline = () => {
  const timelineRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    timelineRef.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const phases = [
    {
      phase: 'Phase 1',
      title: 'Enrollment & Onboarding',
      duration: 'Week 1',
      description: 'Complete enrollment, get access to learning materials and join orientation session',
      icon: <FiCalendar />,
      tasks: ['Complete Registration', 'Payment Processing', 'Resource Access', 'Orientation Session']
    },
    {
      phase: 'Phase 2',
      title: 'Intensive Training',
      duration: 'Weeks 2-5',
      description: 'Live interactive sessions covering all core concepts and practical skills',
      icon: <FiBookOpen />,
      tasks: ['Live Sessions', 'Hands-on Practice', 'Assignments', 'Doubt Clearing']
    },
    {
      phase: 'Phase 3',
      title: 'Industry Guidance',
      duration: 'Week 6',
      description: 'Direct mentorship session with industry professionals',
      icon: <FiUsers />,
      tasks: ['Industry Expert Session', 'Career Guidance', 'Project Planning', 'Roadmap Creation']
    },
    {
      phase: 'Phase 4',
      title: 'Live Internship',
      duration: 'Weeks 7-9',
      description: 'Work on real industry projects with continuous guidance',
      icon: <FiBriefcase />,
      tasks: ['Live Projects', 'Mentor Support', 'Progress Reviews', 'Skill Application']
    },
    {
      phase: 'Phase 5',
      title: 'Certificate & Placement',
      duration: 'Week 10',
      description: 'Receive certificates and career support services',
      icon: <FiAward />,
      tasks: ['Certificate Issuance', 'Resume Building', 'LinkedIn Optimization', 'Interview Prep']
    },
    {
      phase: 'Bonus',
      title: 'Lifetime Benefits',
      duration: 'Forever',
      description: 'Continued access to resources and community support',
      icon: <FiStar />,
      tasks: ['Resource Access', 'Community Network', 'Job Updates', 'Lifetime Support']
    }
  ];

  return (
    <section id="timeline" className="timeline-section section-padding">
      <div className="timeline-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="timeline-header"
        >
          <h2 className="section-title">Program Timeline</h2>
          <p className="section-subtitle">
            A structured 2-month journey from learning to professional experience
          </p>
        </motion.div>

        <div className="timeline-items">
          {phases.map((phase, index) => (
            <div
              key={phase.phase}
              ref={(el) => (timelineRef.current[index] = el)}
              className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'} slide-in`}
            >
              <div className="timeline-content">
                <div className="phase-header">
                  <div className="phase-icon">
                    {React.cloneElement(phase.icon, {
                      size: 24,
                      color: '#ffffff'
                    })}
                  </div>
                  <div className="phase-info">
                    <div className="phase-tag">{phase.phase}</div>
                    <h3 className="phase-title">{phase.title}</h3>
                    <div className="phase-duration">{phase.duration}</div>
                  </div>
                </div>

                <p className="phase-description">{phase.description}</p>

                <div className="tasks-list">
                  {phase.tasks.map((task, taskIndex) => (
                    <div key={taskIndex} className="task-item">
                      <div className="task-marker" />
                      <span className="task-text">{task}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="timeline-connector">
                <div className="connector-line" />
                <div className="connector-dot" />
              </div>
            </div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="summary"
        >
          <div className="summary-card">
            <h3 className="summary-title">Total Duration: 10 Weeks</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <div className="summary-number">4</div>
                <div className="summary-label">Weeks Training</div>
              </div>
              <div className="summary-divider" />
              <div className="summary-item">
                <div className="summary-number">1</div>
                <div className="summary-label">Week Guidance</div>
              </div>
              <div className="summary-divider" />
              <div className="summary-item">
                <div className="summary-number">3</div>
                <div className="summary-label">Weeks Internship</div>
              </div>
              <div className="summary-divider" />
              <div className="summary-item">
                <div className="summary-number">2</div>
                <div className="summary-label">Certificates</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Timeline;